import { FC, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DateRangePicker,
  RadixTable,
  TColumn,
} from '@features/ui';
import { ExportIcon } from '@features/ui/icons';
import { DateValueType, downloadFile } from '@lib/utils';
import {
  TReportEvalAccountAnalysisDetailRow,
  TReportEvalAccountAnalysisSummaryRow,
} from '@src/types/reports';
import {
  exportEvalAccountAnalysis,
  getEvalAccountAnalysis,
} from '@src/services/reports';
import { toast } from 'react-toastify';

export const EvalAnalysisByCreationDate: FC = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [title, setTitle] = useState('EVAL Account Analysis');
  const [titleSummary, setTitleSummary] = useState('EVAL Accounts Summary');
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [analysisData, setAnalysisData] = useState<
    TReportEvalAccountAnalysisDetailRow[]
  >([]);
  const [summaryData, setSummaryData] = useState<
    TReportEvalAccountAnalysisSummaryRow[]
  >([]);

  const hasRequiredDates = Boolean(valueStart?.startDate && valueEnd?.startDate);

  const formatDateParam = (date: Date | undefined | null): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const getSelectedDateParams = () => {
    const startDate = formatDateParam(valueStart?.startDate);
    const endDate = formatDateParam(valueEnd?.startDate);

    if (!startDate || !endDate) {
      toast.error('Start Date and End Date are required');

      return null;
    }

    return {
      start_date: startDate,
      end_date: endDate,
    };
  };

  const handleGetData = async () => {
    const params = getSelectedDateParams();
    if (!params) return;

    setIsLoading(true);
    try {
      const result = await getEvalAccountAnalysis(params);

      setTitle(result.title);
      setTitleSummary(result.title_summary);
      setGeneratedAt(result.generated_at);
      setAnalysisData(result.detail_rows);
      setSummaryData(result.summary_rows);
    } catch (error) {
      toast.error('Failed to fetch report data. Please try again.');
      console.error('Error fetching eval account analysis report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    const params = getSelectedDateParams();
    if (!params) return;

    setIsExporting(true);
    try {
      const response = await exportEvalAccountAnalysis(params);

      downloadFile(response.data, 'eval-account-analysis');
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export report. Please try again.');
      console.error('Error exporting eval account analysis report:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const analysisColumns = useMemo(
    (): TColumn<TReportEvalAccountAnalysisDetailRow>[] => [
      {
        title: 'Packages',
        render: (item) => <div className="text-sm">{item?.package}</div>,
      },
      {
        title: 'Total Accounts',
        render: (item) => (
          <div className="text-center text-sm font-mono">
            {item?.total_accounts}
          </div>
        ),
      },
      {
        title: 'Avg Days',
        render: (item) => (
          <div className="text-center text-sm font-mono">{item?.avg_days}</div>
        ),
      },
      {
        title: 'Resets',
        render: (item) => (
          <div className="text-center text-sm font-mono">{item?.resets}</div>
        ),
      },
      {
        title: 'Resets %',
        render: (item) => (
          <div className="text-center text-sm font-mono">
            {item?.reset_percent}
          </div>
        ),
      },
      {
        title: '5 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_5_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_5_days}
            </span>
          </div>
        ),
      },
      {
        title: '10 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_10_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_10_days}
            </span>
          </div>
        ),
      },
      {
        title: '14 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_14_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_14_days}
            </span>
          </div>
        ),
      },
      {
        title: '30 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_30_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_30_days}
            </span>
          </div>
        ),
      },
      {
        title: '60 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_60_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_60_days}
            </span>
          </div>
        ),
      },
      {
        title: '90 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_90_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_90_days}
            </span>
          </div>
        ),
      },
      {
        title: '120 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_120_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_120_days}
            </span>
          </div>
        ),
      },
      {
        title: '150 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_150_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_150_days}
            </span>
          </div>
        ),
      },
      {
        title: '180 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_180_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_180_days}
            </span>
          </div>
        ),
      },
      {
        title: '210 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_210_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_210_days}
            </span>
          </div>
        ),
      },
      {
        title: '240 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_240_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_240_days}
            </span>
          </div>
        ),
      },
      {
        title: '270 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_270_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_270_days}
            </span>
          </div>
        ),
      },
      {
        title: '300 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_300_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_300_days}
            </span>
          </div>
        ),
      },
      {
        title: '330 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_330_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_330_days}
            </span>
          </div>
        ),
      },
      {
        title: '360 days+',
        render: (item) => (
          <div className="flex flex-col items-center text-xs font-mono">
            <span>{item?.accounts_360_days}</span>
            <span className="text-muted-foreground">
              {item?.percent_360_days}
            </span>
          </div>
        ),
      },
    ],
    []
  );

  const summaryColumns = useMemo(
    (): TColumn<TReportEvalAccountAnalysisSummaryRow>[] => [
      {
        title: 'Status',
        render: (item) => <div className="text-sm">{item?.description}</div>,
      },
      {
        title: 'Amount',
        render: (item) => (
          <div className="text-center text-sm font-mono">
            {item?.amount.toLocaleString()}
          </div>
        ),
      },
      {
        title: '%',
        render: (item) => (
          <div className="text-center text-sm font-mono">{item?.percent}</div>
        ),
      },
      {
        title: 'Avg Days',
        render: (item) => (
          <div className="text-center text-sm font-mono">
            {item?.avg_days !== null ? item?.avg_days : '-'}
          </div>
        ),
      },
      {
        title: 'Max Days',
        render: (item) => (
          <div className="text-center text-sm font-mono">
            {item?.max_days !== null ? item?.max_days : '-'}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground uppercase">
          ACCOUNTS THAT REACHED / EXCEED NUMBER OF DAYS
        </p>
      </div>

      {/* Date Range Filter */}
      <Card>
        <div className="flex items-end gap-4">
          <div className="flex gap-4">
            <DateRangePicker
              asSingle={true}
              className="max-w-[239px]"
              placeholder="Created From"
              size="sm"
              value={valueStart}
              variant="outline"
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className="max-w-[239px]"
              placeholder="Created To"
              size="sm"
              value={valueEnd}
              variant="outline"
              onChange={setValueEnd}
            />
          </div>
          <Button
            className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
            disabled={!hasRequiredDates || isLoading || isExporting}
            onClick={handleGetData}
          >
            {isLoading ? 'Loading...' : 'Run Report'}
          </Button>
          <Button
            className="h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit"
            disabled={!hasRequiredDates || isExporting || isLoading}
            variant="outline"
            onClick={handleExport}
          >
            <p>{isExporting ? 'Exporting...' : 'Export'}</p>
            <ExportIcon />
          </Button>
        </div>
      </Card>

      {/* Analysis Table */}
      <Card>
        <RadixTable columns={analysisColumns} data={analysisData} />
      </Card>

      {/* Summary Table */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">{titleSummary}</h2>
        <p className="text-sm text-muted-foreground">
          data exported {generatedAt ?? '-'}
        </p>

        <Card>
          <RadixTable columns={summaryColumns} data={summaryData} />
        </Card>
      </div>
    </div>
  );
};
